// w?? Sokin — preview thumbnail. The full design lives at
// /templates/sokin.html (off-cream + matte black + a single gold
// hairline, Italiana display) and renders byte-identical inside the
// matching variant's iframe. This 5:7 thumbnail mirrors that extreme
// negative-space, editorial calm: names stacked vertically with a
// tiny italic ampersand, one horizontal gold rule, everything else
// hairline-thin.

import type { LayoutCtx } from "./_shared";

export function SokinPreviewLayout({ names, date, venue, accent, ink, bg, font }: LayoutCtx) {
  const [a, b] = names.split(/\s*&\s*/);
  // Single gold hairline — the only ornament. The accent token from
  // data.ts may not match exactly, so we hardcode the design's gold.
  const gold = "#B7A26E";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: bg,
        color: ink,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "14% 10%",
      }}
    >
      {/* Tiny eyebrow — no decoration, just whitespace and letterspacing */}
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 6.5,
          fontWeight: 500,
          letterSpacing: "0.5em",
          textTransform: "uppercase",
          color: ink,
          opacity: 0.55,
          marginBottom: 28,
        }}
      >
        Taklifnoma
      </div>

      {/* Names stacked vertically with a tiny italic & between */}
      <div
        style={{
          fontFamily: font,
          fontWeight: 400,
          fontSize: 28,
          lineHeight: 1.05,
          color: ink,
          letterSpacing: "0.04em",
        }}
      >
        {a ?? names}
      </div>
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 13,
          color: ink,
          opacity: 0.6,
          margin: "10px 0",
          lineHeight: 1,
        }}
      >
        &
      </div>
      <div
        style={{
          fontFamily: font,
          fontWeight: 400,
          fontSize: 28,
          lineHeight: 1.05,
          color: ink,
          letterSpacing: "0.04em",
        }}
      >
        {b ?? ""}
      </div>

      {/* THE single gold hairline — design signature */}
      <div
        aria-hidden
        style={{
          width: "32%",
          height: 1,
          background: gold,
          margin: "30px 0 22px",
        }}
      />

      {/* Date — restrained, mono-spaced numerics */}
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 7.5,
          fontWeight: 500,
          letterSpacing: "0.4em",
          color: ink,
          fontFeatureSettings: '"tnum" 1, "lnum" 1',
          opacity: 0.85,
        }}
      >
        {date.replace(/\./g, " · ")}
      </div>

      {/* Venue — italic body, very muted */}
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 11,
          color: ink,
          opacity: 0.55,
          marginTop: 8,
          letterSpacing: "0.02em",
        }}
      >
        {venue}
      </div>

      {/* Suppress unused warning for accent — palette is monochrome by design */}
      <span aria-hidden style={{ display: "none", color: accent }} />
    </div>
  );
}
